import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Make sure axios always sends + receives the httpOnly auth cookie.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Shared so many parallel 401s trigger only one refresh call.
let refreshInFlight = null;

export const AuthProvider = ({ children }) => {
  // null = unknown (still checking); object = logged in; false = logged out
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data.user);
      return r.data.user;
    } catch (err) {
      // Access cookie expired? Use the refresh cookie once before giving up.
      if (err?.response?.status === 401) {
        try {
          const rr = await axios.post(`${API}/auth/refresh`);
          setUser(rr.data.user);
          return rr.data.user;
        } catch (refreshErr) {
          log.debug('[AuthContext] refresh on load failed', refreshErr?.message || refreshErr);
        }
      }
      setUser(false);
      return false;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Global 401 handler. The access cookie lasts 12 hours; before kicking the
  // user to the login screen, try ONE silent refresh with the 30-day refresh
  // cookie and replay the request. Previously the refresh endpoint was never
  // called, so everyone was logged out mid-work twice a day.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      async (err) => {
        const original = err?.config;
        const url = original?.url || '';
        const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout');
        if (err?.response?.status === 401 && original && !original._retried && !isAuthCall) {
          original._retried = true;
          try {
            if (!refreshInFlight) {
              refreshInFlight = axios.post(`${API}/auth/refresh`).finally(() => { refreshInFlight = null; });
            }
            await refreshInFlight;
            return axios(original);
          } catch (refreshErr) {
            log.debug('[AuthContext] silent refresh failed', refreshErr?.message || refreshErr);
          }
        }
        if (err?.response?.status === 401 && user && !url.includes('/auth/login')) {
          setUser(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [user]);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Even if the server-side logout fails (e.g. offline), we still drop
      // client state so the user lands on /login. Log for diagnostics.
      log.warn('[AuthContext] logout request failed:', err?.message || err);
    }
    setUser(false);
  }, []);

  const has = useCallback((feature) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return Array.isArray(user.features) && user.features.includes(feature);
  }, [user]);

  const value = { user, checking, login, logout, refresh, has };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
